import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Plus, Building2, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useDebounced } from "@/lib/text";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { CustomerMasterFields, blankMaster, masterPayload } from "@/components/CustomerMasterFields";

export default function CustomerMaster() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const q = useDebounced(search, 300);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blankMaster());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get("/customer-master", { params: q ? { search: q } : {} }).then((r) => setRows(r.data)).finally(() => setLoading(false));
  }, [q]);

  const save = async () => {
    if (!form.legal_name.trim()) { toast.error("Legal Company Name is required"); return; }
    setSaving(true);
    try { const r = await api.post("/customer-master", masterPayload(form)); toast.success(`Created ${r.data.customer_code}`); setOpen(false); navigate(`/customer-master/${r.data.id}`); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  return (
    <div className="p-8 max-w-[1300px] animate-in-up">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Customer Master</h1>
          <p className="text-muted-foreground mt-1">Operational master for PIs, orders and logistics · {rows.length} customer{rows.length !== 1 ? "s" : ""}</p>
        </div>
        <Button data-testid="new-master-button" onClick={() => { setForm(blankMaster()); setOpen(true); }} className="gap-2 font-semibold"><Plus className="h-4 w-4" /> New Customer</Button>
      </div>
      <div className="relative max-w-md mb-5">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input data-testid="master-search-input" placeholder="Search code, name, brand, phone, GSTIN…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
      </div>
      <div className="bg-white border border-border rounded-lg overflow-x-auto">
        {loading ? <div className="p-10 text-center text-sm text-muted-foreground">Loading…</div>
        : rows.length === 0 ? <div className="p-16 text-center"><Building2 className="h-8 w-8 mx-auto text-muted-foreground/50 mb-3" /><p className="font-medium">No customers in the master yet</p><p className="text-sm text-muted-foreground mt-1">Customers are normally created when a PI is entered in the Order Book.</p></div>
        : (
          <table className="w-full text-sm min-w-[900px]">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
              <th className="px-4 py-2.5 font-medium">Code</th><th className="px-3 py-2.5 font-medium">Legal Name</th><th className="px-3 py-2.5 font-medium">Brand</th>
              <th className="px-3 py-2.5 font-medium">Country</th><th className="px-3 py-2.5 font-medium">Mobile</th><th className="px-3 py-2.5 font-medium">GSTIN</th><th className="px-3 py-2.5 font-medium text-right">PIs</th>
            </tr></thead>
            <tbody className="divide-y divide-border">
              {rows.map((m) => (
                <tr key={m.id} data-testid={`master-row-${m.id}`} onClick={() => navigate(`/customer-master/${m.id}`)} className="hover:bg-secondary/50 cursor-pointer">
                  <td className="px-4 py-3 font-mono text-xs font-semibold">{m.customer_code}</td>
                  <td className="px-3 py-3 font-semibold">{m.legal_name}<div className="text-xs text-muted-foreground font-normal truncate max-w-[320px]">{m.default_address}</div></td>
                  <td className="px-3 py-3">{m.brand_name || "—"}</td><td className="px-3 py-3 text-muted-foreground">{m.country || "—"}</td>
                  <td className="px-3 py-3 text-xs">{(m.phones || [])[0] || "—"}</td><td className="px-3 py-3 font-mono text-xs">{m.gstin || "—"}</td>
                  <td className="px-3 py-3 text-right">{m.order_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">New Customer Master</DialogTitle><DialogDescription>Legal/shipping master record. A unique CUS- code is assigned on save.</DialogDescription></DialogHeader>
          <CustomerMasterFields form={form} setForm={setForm} />
          <DialogFooter><Button data-testid="master-save-button" onClick={save} disabled={saving} className="gap-2 font-semibold">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
